import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useAdminApi } from "../../hooks/useAdminApi";
import { Upload, X } from "lucide-react";

const DAYS = [
  { label: "Monday", weekday: "mon" },
  { label: "Tuesday", weekday: "tue" },
  { label: "Wednesday", weekday: "wed" },
  { label: "Thursday", weekday: "thu" },
  { label: "Friday", weekday: "fri" },
  { label: "Saturday", weekday: "sat" },
  { label: "Sunday", weekday: "sun" },
];

const emptyDoctorForm = {
  name: "",
  specialty: "",
  apmc_number: "",
  degrees: "",
  occupation: "",
  photo_url: "",
};

const emptyClinicForm = { name: "", address: "", lat: null, lon: null };

// Extract lat/lon from common Google Maps URL formats:
//   https://www.google.com/maps/place/Name/@17.694,83.217,17z/...
//   https://maps.google.com/maps?q=17.694,83.217
//   https://www.google.com/maps?ll=17.694,83.217
const parseGoogleMapsUrl = (url) => {
  const atMatch = url.match(/@([-\d.]+),([-\d.]+)[,z/]/);
  if (atMatch) return { lat: parseFloat(atMatch[1]), lon: parseFloat(atMatch[2]) };
  const qMatch = url.match(/[?&](?:q|ll)=([-\d.]+),([-\d.]+)/);
  if (qMatch) return { lat: parseFloat(qMatch[1]), lon: parseFloat(qMatch[2]) };
  return null;
};

const emptyAvailability = () =>
  DAYS.map(({ weekday }) => ({ weekday, start_time: "", end_time: "" }));

const Field = ({ label, value, onChange, placeholder, disabled }) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || label}
      disabled={disabled}
    />
  </div>
);

const UnifiedDoctorForm = ({ open, doctor, onSave, onCancel }) => {
  const {
    createDoctor,
    updateDoctor,
    createClinic,
    updateClinic,
    getDoctorAvailability,
    updateDoctorAvailability,
    uploadDoctorPhoto,
    resolveMapsUrl,
  } = useAdminApi();

  // Stable ref so useEffect doesn't re-fire due to unstable api object
  const getDoctorAvailabilityRef = useRef(getDoctorAvailability);
  getDoctorAvailabilityRef.current = getDoctorAvailability;

  const [doctorForm, setDoctorForm] = useState(emptyDoctorForm);
  const [clinicForm, setClinicForm] = useState(emptyClinicForm);
  const [mapsUrl, setMapsUrl] = useState("");
  const [availability, setAvailability] = useState(emptyAvailability());
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formTab, setFormTab] = useState("doctor");

  // Use doctor.id as the dep — avoids re-firing when parent re-renders with a new object reference
  const doctorId = doctor?.id ?? null;

  useEffect(() => {
    if (!open) return;
    if (doctor) {
      setDoctorForm({
        name: doctor.name || "",
        specialty: doctor.specialty || "",
        apmc_number: doctor.apmc_number || "",
        degrees: doctor.degrees || "",
        occupation: doctor.occupation || "",
        photo_url: doctor.photo_url || "",
      });
      setClinicForm({
        name: doctor.clinic_name || "",
        address: doctor.clinic_address || doctor.address || "",
        lat: null,
        lon: null,
      });
      getDoctorAvailabilityRef.current(doctor.id)
        .then(({ data }) => {
          if (Array.isArray(data) && data.length) {
            const merged = DAYS.map(({ weekday }) => {
              const found = data.find((a) => a.weekday === weekday);
              return {
                weekday,
                start_time: found?.start_time || "",
                end_time: found?.end_time || "",
              };
            });
            setAvailability(merged);
          } else {
            setAvailability(emptyAvailability());
          }
        })
        .catch(() => setAvailability(emptyAvailability()));
    } else {
      setDoctorForm(emptyDoctorForm);
      setClinicForm(emptyClinicForm);
      setAvailability(emptyAvailability());
    }
    setPendingPhotoFile(null);
    setPhotoPreview(null);
    setMapsUrl("");
    setFormTab("doctor");
  }, [open, doctorId]); // eslint-disable-line

  const setDoctorField = (field) => (value) =>
    setDoctorForm((prev) => ({ ...prev, [field]: value }));

  const setClinicField = (field) => (value) =>
    setClinicForm((prev) => ({ ...prev, [field]: value }));

  const setAvailField = (weekday, field) => (value) =>
    setAvailability((prev) =>
      prev.map((a) => (a.weekday === weekday ? { ...a, [field]: value } : a))
    );

  const handleExtractCoords = async () => {
    const trimmed = mapsUrl.trim();
    if (!trimmed) return;

    // Short URL (goo.gl) or any URL without visible coords — ask backend to resolve
    const needsResolve = trimmed.includes("goo.gl") || !trimmed.includes("/@");
    if (needsResolve) {
      setResolving(true);
      try {
        const { data } = await resolveMapsUrl(trimmed);
        setClinicForm((prev) => ({ ...prev, lat: data.lat, lon: data.lon }));
        setMapsUrl("");
      } catch {
        // error toast handled by useApi
      } finally {
        setResolving(false);
      }
      return;
    }

    // Full URL — parse client-side
    const coords = parseGoogleMapsUrl(trimmed);
    if (coords) {
      setClinicForm((prev) => ({ ...prev, lat: coords.lat, lon: coords.lon }));
      setMapsUrl("");
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const clearPendingPhoto = () => {
    setPendingPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send days that have both start and end times
      const filledSlots = availability.filter(
        (a) => a.start_time && a.end_time
      );

      if (!doctor) {
        // CREATE — clinic first (need clinic_id for doctor FK)
        const { data: clinicData } = await createClinic(clinicForm);
        const clinicId = clinicData?.id;
        const { data: doctorData } = await createDoctor(doctorForm);
        if (filledSlots.length) {
          await updateDoctorAvailability(doctorData.id, {
            clinic_id: clinicId,
            availability: filledSlots,
          });
        }
        let finalPhotoUrl = doctorData.photo_url;
        if (pendingPhotoFile) {
          setUploading(true);
          try {
            const fd = new FormData();
            fd.append("file", pendingPhotoFile);
            const { data: photoData } = await uploadDoctorPhoto(doctorData.id, fd);
            finalPhotoUrl = photoData?.photo_url;
          } finally {
            setUploading(false);
          }
        }
        onSave({ ...doctorData, photo_url: finalPhotoUrl });
      } else {
        // EDIT — clinic + doctor in parallel, then availability
        await Promise.all([
          updateClinic(doctor.clinic_id, clinicForm),
          updateDoctor(doctor.id, doctorForm),
        ]);
        if (filledSlots.length) {
          await updateDoctorAvailability(doctor.id, {
            clinic_id: doctor.clinic_id,
            availability: filledSlots,
          });
        }
        let finalPhotoUrl = doctorForm.photo_url;
        if (pendingPhotoFile) {
          setUploading(true);
          try {
            const fd = new FormData();
            fd.append("file", pendingPhotoFile);
            const { data: photoData } = await uploadDoctorPhoto(doctor.id, fd);
            finalPhotoUrl = photoData?.photo_url;
          } finally {
            setUploading(false);
          }
        }
        onSave({ ...doctor, ...doctorForm, photo_url: finalPhotoUrl, clinic_name: clinicForm.name });
      }
    } catch {
      // useApi showToast:true already surfaces error toasts
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doctor ? "Edit Doctor" : "Add Doctor"}</DialogTitle>
        </DialogHeader>

        <Tabs value={formTab} onValueChange={setFormTab}>
          <TabsList className="w-full">
            <TabsTrigger value="doctor" className="flex-1">Doctor Info</TabsTrigger>
            <TabsTrigger value="clinic" className="flex-1">Clinic</TabsTrigger>
            <TabsTrigger value="availability" className="flex-1">Availability</TabsTrigger>
          </TabsList>

          {/* Tab 1 — Doctor Info */}
          <TabsContent value="doctor" className="pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Field label="Full Name" value={doctorForm.name} onChange={setDoctorField("name")} disabled={saving} />
              </div>
              <Field label="Specialty" value={doctorForm.specialty} onChange={setDoctorField("specialty")} disabled={saving} />
              <Field label="APMC Number" value={doctorForm.apmc_number} onChange={setDoctorField("apmc_number")} disabled={saving} />
              <div className="col-span-2">
                <Field label="Degrees (comma-separated)" value={doctorForm.degrees} onChange={setDoctorField("degrees")} placeholder="MBBS, MD" disabled={saving} />
              </div>
              <div className="col-span-2">
                <Field label="Occupation / Title" value={doctorForm.occupation} onChange={setDoctorField("occupation")} placeholder="e.g. Cardiologist" disabled={saving} />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700">Doctor Photo</label>
                <div className="mt-2 flex items-center gap-4">
                  {(photoPreview || doctorForm.photo_url) ? (
                    <img
                      src={photoPreview || doctorForm.photo_url}
                      alt="Doctor"
                      className="w-16 h-16 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      className="hidden"
                      onChange={handlePhotoSelect}
                      disabled={saving || uploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving || uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      {uploading ? "Uploading..." : pendingPhotoFile ? "Change Photo" : "Upload Photo"}
                    </Button>
                    {pendingPhotoFile && (
                      <button
                        type="button"
                        onClick={clearPendingPhoto}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500"
                        disabled={saving || uploading}
                      >
                        <X className="w-3 h-3" /> {pendingPhotoFile.name}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2 — Clinic */}
          <TabsContent value="clinic" className="pt-4 space-y-4">
            <Field label="Clinic Name" value={clinicForm.name} onChange={setClinicField("name")} disabled={saving} />
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Address</label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={clinicForm.address}
                onChange={(e) => setClinicField("address")(e.target.value)}
                placeholder="Full clinic address"
                disabled={saving}
              />
            </div>

            {/* Google Maps URL → lat/lon */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Google Maps URL</label>
              <div className="flex gap-2">
                <Input
                  value={mapsUrl}
                  onChange={(e) => setMapsUrl(e.target.value)}
                  placeholder="Paste Google Maps link to auto-fill coordinates"
                  disabled={saving}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={saving || resolving || !mapsUrl.trim()}
                  onClick={handleExtractCoords}
                >
                  {resolving ? "Resolving..." : "Extract"}
                </Button>
              </div>
              {clinicForm.lat != null && clinicForm.lon != null && (
                <p className="text-xs text-green-700 mt-1">
                  Location set: {clinicForm.lat.toFixed(6)}, {clinicForm.lon.toFixed(6)}
                  <button
                    type="button"
                    className="ml-2 text-gray-400 hover:text-red-500"
                    onClick={() => setClinicForm((prev) => ({ ...prev, lat: null, lon: null }))}
                  >
                    ✕ clear
                  </button>
                </p>
              )}
            </div>
          </TabsContent>

          {/* Tab 3 — Availability (start_time / end_time per weekday) */}
          <TabsContent value="availability" className="pt-4 space-y-3">
            <p className="text-xs text-gray-500">Leave blank to mark a day as unavailable. Use 24-hour format (e.g. 09:00, 17:30).</p>
            {DAYS.map(({ label, weekday }) => {
              const entry = availability.find((a) => a.weekday === weekday);
              return (
                <div key={weekday} className="grid grid-cols-3 gap-3 items-center">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Start</label>
                    <Input
                      type="time"
                      value={entry?.start_time || ""}
                      onChange={(e) => setAvailField(weekday, "start_time")(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">End</label>
                    <Input
                      type="time"
                      value={entry?.end_time || ""}
                      onChange={(e) => setAvailField(weekday, "end_time")(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
              );
            })}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {saving ? "Saving..." : doctor ? "Update Doctor" : "Create Doctor"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedDoctorForm;
